import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect } from "react";
import { youTubeId } from "#/components/shorts-player";
import { DefaultLandingTemplate } from "#/components/templates/default-landing";
import { asTemplateKey } from "#/components/templates/registry";
import { VideoLandingTemplate } from "#/components/templates/video-landing";
import { setMetaPixelId } from "#/lib/analytics";
import { PARCOURS_SLUG } from "#/lib/entrepreneur-101";
import { asFeedbackVideos } from "#/lib/feedback-videos";
import type { Dict, Locale } from "#/lib/i18n";
import { useI18n } from "#/lib/i18n";
import { getTrainingBySlug } from "#/lib/training";

export const Route = createFileRoute("/training/$slug")({
	// The Entrepreneur 101 path is a training row (its leads are filed there)
	// whose landing page is the home page — never a page of its own.
	beforeLoad: ({ params }) => {
		if (params.slug === PARCOURS_SLUG) throw redirect({ to: "/" });
	},
	loader: ({ params }) => getTrainingBySlug({ data: params.slug }),
	// Static SEO head sourced from the training's own Arabic content (primary
	// market) — same convention the old single-training "/" route used.
	head: ({ loaderData }) => {
		if (!loaderData) return {};
		const content = loaderData.content as Record<Locale, Dict>;
		const { title, description } = content.ar.meta;

		return {
			meta: [
				{ title },
				{ name: "description", content: description },
				{ property: "og:title", content: title },
				{ property: "og:description", content: description },
				{
					"script:ld+json": {
						"@context": "https://schema.org",
						"@type": "Course",
						name: title,
						description,
						provider: {
							"@type": "Organization",
							name: "أكاديمية مستشير",
							sameAs: "https://www.moustachir.dz",
						},
						inLanguage: "ar",
						hasCourseInstance: {
							"@type": "CourseInstance",
							courseMode: "Onsite",
							courseWorkload: "PT18H",
						},
					},
				},
			],
			// Per-training Meta Pixel — see MULTI-TRAINING-PLAN.md: pixel config
			// lives on the Training row now, not a single site-wide env var.
			scripts: loaderData.metaPixelId
				? [
						{
							children: `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${loaderData.metaPixelId}');fbq('track','PageView');console.log('[Meta Pixel] connected — pixel', '${loaderData.metaPixelId}', 'for training', '${loaderData.slug}');`,
						},
					]
				: [],
		};
	},
	component: TrainingPage,
});

function TrainingPage() {
	const training = Route.useLoaderData();
	const { locale } = useI18n();
	const content = (training.content as Record<Locale, Dict>)[locale];

	// Keep the tab title in sync on client-side language switches — the head()
	// above only covers the initial SSR render.
	useEffect(() => {
		document.title = content.meta.title;
	}, [content]);

	// Every fbq() call in src/lib/analytics.ts reports against this training's
	// own pixel, not a site-wide one — reset on unmount so a stray event fired
	// while navigating away never attributes to the wrong training.
	useEffect(() => {
		setMetaPixelId(training.metaPixelId);
		return () => setMetaPixelId(null);
	}, [training.metaPixelId]);

	// Assets are resolved once here, so both templates receive plain strings.
	const assets = {
		heroPortraitPath: training.heroPortraitPath ?? "/team/ayoub-portrait.webp",
		heroWorkshopPath: training.heroWorkshopPath ?? "/team/ayoub-training.webp",
		trainerAvatarPath: training.trainerAvatarPath ?? "/team/ayoub-avatar.webp",
		brochurePdfAr:
			training.brochurePdfAr ?? "/downloads/fiche-technique-ar.pdf",
		brochurePdfFr:
			training.brochurePdfFr ?? "/downloads/fiche-technique-fr.pdf",
	};

	const shared = {
		content,
		trainingId: training.id,
		trainingSlug: training.slug,
		messengerUrl: training.messengerUrl,
		whatsappNumber: training.whatsappNumber,
		feedbackVideos: asFeedbackVideos(training.feedbackVideos),
		...assets,
	};

	return (
		<>
			{training.metaPixelId && (
				<noscript>
					{/* Counts visitors with JavaScript disabled or the pixel script blocked. */}
					<img
						height="1"
						width="1"
						alt=""
						style={{ display: "none" }}
						src={`https://www.facebook.com/tr?id=${training.metaPixelId}&ev=PageView&noscript=1`}
					/>
				</noscript>
			)}
			{asTemplateKey(training.template) === "video" ? (
				<VideoLandingTemplate
					{...shared}
					videoId={youTubeId(training.videoUrl)}
					videoPosterPath={training.videoPosterPath}
				/>
			) : (
				<DefaultLandingTemplate {...shared} />
			)}
		</>
	);
}
